import { Globe } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useSubmit } from "react-router"
import { Button } from "#app/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu"
import { useOptionalUser } from "#app/utils/user"

const languages = [
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
]

export function MarketingHeader() {
  const { t, i18n } = useTranslation()
  const user = useOptionalUser()
  const submit = useSubmit()

  const changeLanguage = (lng: string) => {
    // Change language in i18n
    i18n.changeLanguage(lng).catch((error) => {
      console.error('Failed to change language:', error)
    })
    
    // Update cookie by submitting a form
    const formData = new FormData()
    formData.append('lng', lng)
    formData.append('_action', 'setLanguage')
    void submit(formData, { method: 'post', action: '/resources/set-language' })
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-gray-900 dark:text-white font-[family-name:var(--font-heading)]">
            Offerty
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/#features" className="text-sm font-medium hover:text-primary">
              {t('common:navigation.features')}
            </Link>
            <Link to="/pricing" className="text-sm font-medium hover:text-primary">
              {t('common:navigation.pricing')}
            </Link>
            <Link to="/faq-page" className="text-sm font-medium hover:text-primary">
              {t('common:navigation.faq')}
            </Link>
          </nav>

          {/* Right section */}
          <div className="flex items-center space-x-4">
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Globe className="h-4 w-4" />
                  <span className="sr-only">Change language</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? "bg-accent" : ""}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth buttons */}
            {user ? (
              <Button asChild>
                <Link to="/dashboard">{t('common:navigation.dashboard')}</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">{t('common:navigation.login')}</Link>
                </Button>
                <Button asChild>
                  <Link to="/signup">{t('common:navigation.signup')}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}